import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Barcode,
  CheckCircle2,
  Loader2,
  Minus,
  PackagePlus,
  Plus,
  ScanLine,
  Search,
  ShoppingCart,
  Trash2,
  User,
  WifiOff,
} from 'lucide-react';
import { toast } from 'sonner';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { CONTENT_CARD, FieldLabel } from '@/components/ui/field';
import { heroTotalSizeClass } from '@/lib/quoteHero';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ClientSelector } from '@/components/quotes/ClientSelector';
import { CatalogSelector } from '@/components/quotes/CatalogSelector';
import { GenerateReceiptButton } from '@/components/receipts/GenerateReceiptButton';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { useBarcodeScanner } from '@/hooks/useBarcodeScanner';
import { useQuickSale, type QuickSaleResult } from '@/hooks/useQuickSale';
import { useRole } from '@/hooks/useRole';
import { useFeature } from '@/hooks/useFeature';
import { supabase } from '@/integrations/supabase/client';
import { cacheCatalog, getCachedCatalog } from '@/lib/offlineDataCache';
import { PAYMENT_METHOD_LABELS, formatBRL } from '@/lib/financeLabels';
import { todayIsoDate } from '@/lib/financeStatus';
import { downloadQuotePDF, type PDFQuoteData } from '@/lib/pdfGenerator';
import { ThermalPrintButton, downloadThermalPdf } from '@/components/pdf/ThermalPrintButton';
import { hexToRgb } from '@/lib/pdfTemplates/shared';
import { cacheBundles, getCachedBundles } from '@/lib/offlineDataCache';
import {
  groupBundleItems,
  resolveBundleItems,
  type BundleItemRow,
} from '@/lib/productBundles';
import { plural } from '@/lib/plural';
import {
  QUICK_SALE_TITLE,
  WALK_IN_CUSTOMER_NAME,
  addQuickSaleBundleItems,
  addQuickSaleItem,
  buildQuickSaleStockLines,
  canFinalizeQuickSale,
  findQuickSaleProductByBarcode,
  quickSaleTotal,
  removeQuickSaleItem,
  searchQuickSaleProducts,
  setQuickSaleQuantity,
  type QuickSaleCartItem,
} from '@/lib/quickSale';
import type { Client, PaymentMethod, Product } from '@/types/database';
import { MODULE_LABELS } from '@/lib/labels';

const PAYMENT_METHODS = Object.keys(PAYMENT_METHOD_LABELS) as PaymentMethod[];

interface CompletedSale {
  result: QuickSaleResult;
  cart: QuickSaleCartItem[];
  client: Client | null;
  paymentMethod: PaymentMethod;
}

function QuantityInput({
  item,
  onChange,
}: {
  item: QuickSaleCartItem;
  onChange: (quantity: number) => void;
}) {
  const [raw, setRaw] = useState(String(item.quantity).replace('.', ','));

  useEffect(() => {
    setRaw(String(item.quantity).replace('.', ','));
  }, [item.quantity]);

  const commit = () => {
    const parsed = Number(raw.replace(',', '.'));
    if (Number.isFinite(parsed) && parsed > 0) {
      onChange(parsed);
    } else {
      setRaw(String(item.quantity).replace('.', ','));
    }
  };

  return (
    <Input
      aria-label={`Quantidade de ${item.name}`}
      inputMode="decimal"
      value={raw}
      onChange={(event) => setRaw(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          commit();
          event.currentTarget.blur();
        }
      }}
      className="h-9 w-20 text-center"
    />
  );
}

export default function QuickSalePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const { isOnline } = useNetworkStatus();
  const { canWrite, canSeeFinance } = useRole();
  const noBrandingFooter = useFeature('no_branding_footer');
  const finalizeSale = useQuickSale();
  const { scan, isAvailable: scannerAvailable, isScanning } = useBarcodeScanner();

  const [products, setProducts] = useState<Product[]>([]);
  const [bundleRows, setBundleRows] = useState<BundleItemRow[]>([]);
  const [cart, setCart] = useState<QuickSaleCartItem[]>([]);
  const [search, setSearch] = useState('');
  const [client, setClient] = useState<Client | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('pix');
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [clientOpen, setClientOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [completed, setCompleted] = useState<CompletedSale | null>(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  const total = quickSaleTotal(cart);
  const stockLines = useMemo(() => buildQuickSaleStockLines(cart), [cart]);
  const negativeStock = useMemo(
    () => stockLines.filter((line) => line.projectedBalance < 0),
    [stockLines],
  );
  const searchResults = useMemo(
    () => searchQuickSaleProducts(products, search),
    [products, search],
  );
  const showScannerButton = scannerAvailable || import.meta.env.DEV;

  useEffect(() => {
    if (!user?.id || !activeCompany?.id) return;

    const cached = (getCachedCatalog(user.id) ?? []) as Product[];
    const scoped = cached.filter(
      (product) => !product.company_id || product.company_id === activeCompany.id,
    );
    setProducts(scoped);

    if (!isOnline) return;
    supabase
      .from('products')
      .select('*')
      .eq('company_id', activeCompany.id)
      .order('name')
      .then(({ data, error }) => {
        if (!error && data) {
          setProducts(data as Product[]);
          cacheCatalog(user.id, data);
        }
      });
  }, [user?.id, activeCompany?.id, isOnline]);

  // Vínculos de kit — cache primeiro, servidor depois: vender um serviço-kit
  // precisa funcionar sem rede, como o resto do balcão.
  useEffect(() => {
    if (!user?.id) return;

    const cached = getCachedBundles(user.id);
    if (cached) setBundleRows(cached as BundleItemRow[]);

    if (!isOnline || !activeCompany?.id) return;
    let cancelled = false;
    supabase
      .from('product_bundle_items')
      .select('*')
      .eq('company_id', activeCompany.id)
      .then(({ data, error }) => {
        if (cancelled || error || !data) return;
        setBundleRows(data as BundleItemRow[]);
        cacheBundles(user.id, data);
      });

    return () => {
      cancelled = true;
    };
  }, [user?.id, activeCompany?.id, isOnline]);

  const bundlesByParent = useMemo(() => groupBundleItems(bundleRows), [bundleRows]);

  /**
   * Acrescenta o produto e, se ele for um serviço-kit, os materiais inclusos.
   *
   * Os materiais entram como LINHAS DO CARRINHO, visíveis e removíveis, antes
   * de qualquer baixa — é essa visibilidade que torna aceitável expandir kit
   * num fluxo que baixa estoque sozinho (ver `src/lib/quickSale.ts`).
   */
  const addToCart = (cartNow: QuickSaleCartItem[], product: Product): QuickSaleCartItem[] => {
    let next = addQuickSaleItem(cartNow, product, 1, products);

    const bundle = bundlesByParent.get(String(product.id));
    if (!bundle || bundle.length === 0) return next;

    const resolved = resolveBundleItems(bundle, products);
    if (resolved.length === 0) return next;

    next = addQuickSaleBundleItems(
      next,
      resolved.map((material) => ({
        product: material.product as Product,
        quantity: material.quantity,
        displayName: material.displayName,
      })),
      {
        parentId: String(product.id),
        // A quantidade do serviço JÁ no carrinho depois do add acima: ler daqui
        // (e não "1") mantém o material coerente com um serviço que o operador
        // já tinha bipado antes.
        parentQuantity: next.find((item) => item.product_id === product.id)?.quantity ?? 1,
        allProducts: products,
      },
    );
    return next;
  };

  const addProduct = (product: Product) => {
    setCart((current) => {
      const next = addToCart(current, product);
      // Linhas que aparecem sozinhas precisam ser anunciadas — contar as que
      // ganharam origem de kit é mais direto (e mais robusto) do que deduzir
      // pela diferença de tamanho do carrinho.
      const materials = next.filter(
        (item) =>
          item.bundle_parent_id === String(product.id) &&
          !current.some((old) => old.product_id === item.product_id),
      ).length;
      if (materials > 0) {
        toast.info(
          `${plural(materials, 'material incluso foi adicionado', 'materiais inclusos foram adicionados')} ao carrinho.`,
        );
      }
      return next;
    });
    setSearch('');
    toast.success(`Adicionado: ${product.name}`);
  };

  const handleScan = async () => {
    const code = await scan();
    if (!code) return;
    const product = findQuickSaleProductByBarcode(products, code);
    if (!product) {
      toast.warning('Código não encontrado no catálogo.');
      return;
    }
    addProduct(product);
  };

  const handleCatalogSelect = (selected: Product[]) => {
    setCart((current) => selected.reduce((next, product) => addToCart(next, product), current));
  };

  const handleFinalize = async () => {
    if (!canWrite) {
      toast.warning('Você tem acesso somente leitura nesta empresa.', { id: 'readonly-access' });
      return;
    }
    const snapshot = cart.map((item) => ({ ...item }));
    try {
      const result = await finalizeSale.mutateAsync({ cart: snapshot, client, paymentMethod });
      setCompleted({ result, cart: snapshot, client, paymentMethod });
      setCart([]);
      setConfirmOpen(false);
      toast.success(
        result.offline
          ? 'Venda salva no aparelho e pronta para sincronizar.'
          : 'Venda concluída com sucesso!',
        result.offline ? { icon: '📴' } : undefined,
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Não foi possível finalizar a venda.';
      toast.error(message);
    }
  };

  /**
   * Dados do documento da venda concluída. Fonte ÚNICA do A4 e do cupom
   * térmico: se cada botão montasse o seu, uma correção num deles (o total, a
   * flag de branding) deixaria o outro para trás sem erro nenhum.
   */
  const buildSalePdfData = (): PDFQuoteData => {
    const company = activeCompany;
    return {
      documentType: 'pedido',
      client: {
        name: completed?.client?.name || WALK_IN_CUSTOMER_NAME,
        phone: completed?.client?.phone,
        address: completed?.client?.address,
        document: completed?.client?.document,
      },
      company: company
        ? {
            name: company.name,
            cnpj: company.cnpj_cpf,
            address: company.address,
            phone: company.phone,
            email: company.email,
            logoUrl: company.logo_url,
            pixKey: company.pix_key,
            pixCity: company.pix_city,
          }
        : undefined,
      quoteDate: todayIsoDate(),
      items: (completed?.cart ?? []).map((item) => ({
        name: item.name,
        description: item.description,
        quantity: item.quantity,
        unit_price: item.unit_price,
        discount: item.discount,
        discount_type: 'value',
        unit: item.unit,
        image_url: item.image_url,
      })),
      total: quickSaleTotal(completed?.cart ?? []),
      // PDV não tem frete nem desconto de cabeçalho (ver src/lib/quickSale.ts):
      // o cupom sai com a linha de total e nada a decompor.
      shipping: 0,
      discount: 0,
      discountType: 'value',
      // Mesma flag de todos os documentos: grátis mantém o rodapé;
      // PRO remove. Venda Rápida não ganha exceção de branding.
      isPremium: noBrandingFooter.allowed,
      brandColorRGB: company ? hexToRgb(company.primary_color) : [16, 185, 129],
      template: company?.pdf_template ?? 'classico',
      showSequenceId: false,
      providerSignatureDataUrl: company?.signature_url,
      // Configuração da empresa. `?? true` preserva o comportamento de sempre
      // para quem não abriu a opção (e para empresa ainda não carregada).
      pdvSignatureLine: company?.pdv_signature_line ?? true,
      // Marca-d'agua da logo (config da empresa). `=== true` e nao `Boolean`:
      // ausente/indefinida cai no padrao DESLIGADO, que e o historico.
      logoWatermark: company?.logo_watermark === true,
    };
  };

  const handleGeneratePdf = async () => {
    if (!completed) return;
    setGeneratingPdf(true);
    try {
      await downloadQuotePDF(buildSalePdfData(), `pedido-venda-${Date.now()}.pdf`);
      toast.success('PDF da venda gerado.');
    } catch (error) {
      console.error('[QuickSalePage] PDF error:', error);
      toast.error('Não foi possível gerar o PDF da venda.');
    } finally {
      setGeneratingPdf(false);
    }
  };

  if (!user) {
    return (
      <MainLayout showFab={false}>
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <ShoppingCart className="mb-4 h-14 w-14 text-muted-foreground/50" />
          <h1 className="text-xl font-semibold">{MODULE_LABELS.quickSale.one}</h1>
          <p className="mb-5 text-sm text-muted-foreground">Faça login para vender pelo balcão.</p>
          <Button3D onClick={() => navigate('/auth')}>Fazer login</Button3D>
        </div>
      </MainLayout>
    );
  }

  if (completed) {
    const { result } = completed;
    const financeMessage =
      result.financeStatus === 'created'
        ? 'Receita lançada e marcada como paga.'
        : result.financeStatus === 'queued'
          ? 'Receita será lançada ao sincronizar.'
          : result.financeStatus === 'skipped_permission'
            ? 'Venda concluída; seu perfil não tem permissão para lançar no financeiro.'
            : 'Venda concluída, mas o lançamento financeiro não foi criado.';

    const completedTotal = quickSaleTotal(completed.cart);

    return (
      <MainLayout showFab={false}>
        <div className="pb-4">
          {/* ================================================================ */}
          {/* HERO — o mesmo desenho oficial da tela de venda, agora com o      */}
          {/* valor FECHADO no lugar do total ao vivo.                          */}
          {/* ================================================================ */}
          <div className="relative -mx-4 -mt-6 overflow-hidden rounded-b-[2rem] bg-gradient-to-br from-primary via-primary to-emerald-600 px-4 pb-14 pt-6 text-white shadow-[0_10px_30px_-12px_hsla(var(--primary)/0.6)]">
            <div className="pointer-events-none absolute -right-10 -top-16 h-44 w-44 rounded-full bg-white/10" />
            <div className="pointer-events-none absolute -left-16 top-16 h-40 w-40 rounded-full bg-white/5" />

            <div className="relative flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 shrink-0 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white"
                onClick={() => navigate('/dashboard')}
                aria-label="Voltar"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] uppercase tracking-wide text-white/70">
                  {MODULE_LABELS.quickSale.one}
                </p>
                <h1 className="line-clamp-2 text-lg font-bold leading-tight">Venda concluída</h1>
              </div>
              <CheckCircle2 className="h-7 w-7 shrink-0 text-white/90" aria-hidden="true" />
            </div>

            <div className="relative mt-5 min-w-0">
              <p className="text-xs text-white/75">{QUICK_SALE_TITLE}</p>
              <p
                className={cn(
                  'truncate font-bold leading-tight',
                  heroTotalSizeClass(formatBRL(completedTotal)),
                )}
              >
                {formatBRL(completedTotal)}
              </p>
              <p className="mt-0.5 text-xs text-white/70">
                {result.offline ? 'Aguardando sincronização' : 'Pedido de venda gravado'}
              </p>
            </div>
          </div>

          {/* Conteúdo — sobe e encosta na curva do hero. */}
          <div className="relative z-10 -mt-9 space-y-5">
            {/* O que o app FEZ com a venda: estoque e financeiro. Cada um pode
                ter falhado por conta própria, e calar qualquer um deles é o que
                faria o operador acreditar num lançamento que não existe. */}
            <div className={cn(CONTENT_CARD, 'space-y-2 p-4 text-sm')}>
              <p>
                Estoque:{' '}
                {result.stockRequested === 0
                  ? 'nenhum item rastreado'
                  : `${result.stockApplied} de ${result.stockRequested} baixa(s) ${
                      result.offline ? 'enfileirada(s)' : 'registrada(s)'
                    }`}
              </p>
              {result.stockFailed.length > 0 && (
                <p className="text-amber-700 dark:text-amber-400">
                  Atenção: não foi possível baixar {result.stockFailed.join(', ')}.
                </p>
              )}
              <p
                className={
                  result.financeStatus === 'failed' ||
                  result.financeStatus === 'skipped_permission'
                    ? 'text-amber-700 dark:text-amber-400'
                    : ''
                }
              >
                {financeMessage}
              </p>
            </div>

            {/* `grid-cols-1` explícito (#185): sem ele a coluna implícita é
                `auto`, cujo mínimo é o min-content do item mais largo — o
                rótulo de um destes quatro botões podia esticar a coluna e
                levar a página junto, e `truncate` não protegeria (ele é a
                causa). Defeito pré-existente, achado pelo guard de grade. */}
            <div className="grid grid-cols-1 gap-3">
            <GenerateReceiptButton
              className="w-full"
              prefill={{
                quote_id: result.quoteId,
                finance_entry_id: result.financeEntryId,
                client_id: completed.client?.id ?? null,
                payer_name: completed.client?.name || WALK_IN_CUSTOMER_NAME,
                payer_document: completed.client?.document,
                payer_address: completed.client?.address,
                amount: quickSaleTotal(completed.cart),
                description: QUICK_SALE_TITLE,
                payment_method: completed.paymentMethod,
              }}
            />
            <Button3D className="w-full" onClick={handleGeneratePdf} disabled={generatingPdf}>
              {generatingPdf ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Barcode className="mr-2 h-4 w-4" />
              )}
              {generatingPdf ? 'Gerando PDF...' : 'Gerar PDF'}
            </Button3D>
            {/* Cupom de balcão: é o caso principal da bobina — o cliente está
                aqui, esperando o comprovante da venda que acabou de fechar. */}
            <ThermalPrintButton
              label="Imprimir cupom"
              onPrint={(format) =>
                downloadThermalPdf(buildSalePdfData(), 'pedido-venda', format)
              }
            />
            <Button3D
              color="gray"
              className="w-full"
              onClick={() => {
                setCompleted(null);
                setClient(null);
                setPaymentMethod('pix');
                setSearch('');
              }}
            >
              Nova venda
            </Button3D>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout showFab={false}>
      <div className="pb-4">
        {/* ================================================================== */}
        {/* HERO — o desenho oficial do app (o mesmo de /quotes/new)            */}
        {/* ------------------------------------------------------------------ */}
        {/* O PDV é uma tela de FORMULÁRIO, então o molde transfere quase
            inteiro: o valor ao vivo do cabeçalho é o TOTAL DO CARRINHO, que é
            exatamente o que o balcão está montando — o análogo perfeito do
            total do orçamento em `/quotes/new`.

            O aviso de "sem conexão" subiu para cá como PÍLULA com texto: o
            ícone solto do cabeçalho antigo não dizia o que significava, e no
            balcão essa é a informação que muda o que vai acontecer ao
            confirmar (pedido, estoque e financeiro vão para a fila). */}
        <div className="relative -mx-4 -mt-6 overflow-hidden rounded-b-[2rem] bg-gradient-to-br from-primary via-primary to-emerald-600 px-4 pb-14 pt-6 text-white shadow-[0_10px_30px_-12px_hsla(var(--primary)/0.6)]">
          <div className="pointer-events-none absolute -right-10 -top-16 h-44 w-44 rounded-full bg-white/10" />
          <div className="pointer-events-none absolute -left-16 top-16 h-40 w-40 rounded-full bg-white/5" />

          <div className="relative flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 shrink-0 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white"
              onClick={() => navigate(-1)}
              aria-label="Voltar"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] uppercase tracking-wide text-white/70">Venda de balcão</p>
              <h1 className="line-clamp-2 text-lg font-bold leading-tight">
                {MODULE_LABELS.quickSale.one}
              </h1>
            </div>
            {!isOnline && (
              <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-emerald-950/25 px-2.5 py-1 text-[11px] leading-none">
                <WifiOff className="h-3.5 w-3.5" aria-hidden="true" />
                Sem conexão
              </span>
            )}
          </div>

          <div className="relative mt-5 min-w-0">
            <p className="text-xs text-white/75">Total da venda</p>
            {/* `heroTotalSizeClass` é a FONTE ÚNICA do degrau de tamanho
                (#159-b): sem ele, uma venda acima de ~R$ 100 mil imprimiria o
                número mais importante da tela cortado. */}
            <p
              className={cn(
                'truncate font-bold leading-tight',
                heroTotalSizeClass(formatBRL(total)),
              )}
            >
              {formatBRL(total)}
            </p>
            <p className="mt-0.5 text-xs text-white/70">
              {cart.length === 0
                ? 'Carrinho vazio · não consome sua cota'
                : `${plural(cart.length, 'item no carrinho', 'itens no carrinho')} · não consome sua cota`}
            </p>
          </div>
        </div>

        {/* Conteúdo — sobe e encosta na curva do hero, como em /quotes/new.
            No DESKTOP vira o balcão de duas colunas: a busca ocupa a coluna
            larga e o carrinho + fechamento ficam numa faixa fixa à direita,
            que é onde o operador olha entre um item e outro. Empilhado, ele
            teria de rolar até o fim a cada produto bipado para conferir o
            total.

            `grid-cols-1` explícito no celular NÃO é decoração: sem ele a
            coluna implícita é `auto`, cujo mínimo é o min-content do item mais
            largo — um nome de produto comprido estica a coluna e vaza a
            página inteira (#185). */}
        <div className="relative z-10 -mt-9 grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start">
        <div className="min-w-0 space-y-5">
        <div className={cn(CONTENT_CARD, 'p-4')}>
          <div className="space-y-3">
            <FieldLabel htmlFor="quick-sale-search">Buscar ou bipar produto</FieldLabel>
            <div className="flex gap-2">
              <div className="relative min-w-0 flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="quick-sale-search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Nome ou código de barras"
                  className="pl-9"
                />
              </div>
              {showScannerButton && (
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={handleScan}
                  disabled={isScanning}
                  aria-label="Escanear código de barras"
                >
                  {isScanning ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <ScanLine className="h-5 w-5" />
                  )}
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => setCatalogOpen(true)}
                aria-label="Escolher no catálogo"
              >
                <PackagePlus className="h-5 w-5" />
              </Button>
            </div>

            {searchResults.length > 0 && (
              <div className="divide-y overflow-hidden rounded-xl border">
                {searchResults.map((product) => (
                  <button
                    key={product.id}
                    type="button"
                    className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-muted"
                    onClick={() => addProduct(product)}
                  >
                    {/* Sem `truncate` no nome (#175): é ele que distingue duas
                        linhas do resultado, e cortá-lo devolve a lista de itens
                        idênticos. `min-w-0` + `break-words` para um nome sem
                        espaços não esticar a linha para fora da tela. */}
                    <div className="min-w-0">
                      <p className="min-w-0 break-words text-sm font-medium">{product.name}</p>
                      {product.barcode && (
                        <p className="text-xs text-muted-foreground">{product.barcode}</p>
                      )}
                    </div>
                    <span className="shrink-0 text-sm font-semibold">{formatBRL(product.price)}</span>
                  </button>
                ))}
              </div>
            )}

            <Button
              type="button"
              variant="ghost"
              className="w-full justify-start"
              onClick={() => setClientOpen(true)}
            >
              <User className="mr-2 h-4 w-4" />
              {client ? client.name : 'Vincular cliente (opcional)'}
            </Button>
            {client && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-muted-foreground"
                onClick={() => setClient(null)}
              >
                Remover cliente
              </Button>
            )}
          </div>
        </div>
        </div>

        {/* Coluna do balcão: `sticky` para o total acompanhar a rolagem da
            busca. `items-start` no grid é o que permite isso — com o padrão
            `stretch` a coluna teria a altura da irmã e não haveria o que
            grudar. */}
        <div className="min-w-0 space-y-5 lg:sticky lg:top-6">
        <div className={cn(CONTENT_CARD, 'p-4')}>
          <div className="mb-3 flex items-center justify-between gap-2">
            <span className="flex items-center gap-2 font-semibold">
              <ShoppingCart className="h-5 w-5 text-primary" />
              Carrinho
            </span>
            <Badge variant="secondary">{cart.length}</Badge>
          </div>
          <div className="space-y-3">
            {cart.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                Bipe ou escolha um item do catálogo.
              </p>
            ) : (
              cart.map((item) => (
                <div key={item.product_id} className="space-y-2 rounded-xl border border-border/60 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="min-w-0 break-words font-medium">{item.name}</p>
                      {/* Material que entrou por causa de um serviço-kit: o
                          operador precisa ver de onde veio ANTES de finalizar,
                          porque é dele que o estoque vai sair. */}
                      {item.bundle_parent_id && (
                        <p className="text-[11px] text-muted-foreground">
                          Material incluso em{' '}
                          {cart.find((other) => other.product_id === item.bundle_parent_id)?.name
                            ?? 'um serviço'}
                        </p>
                      )}
                      <p className="text-sm text-muted-foreground">{formatBRL(item.unit_price)} cada</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive"
                      onClick={() =>
                        setCart((current) => removeQuickSaleItem(current, item.product_id))
                      }
                      aria-label={`Remover ${item.name}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-9 w-9"
                        onClick={() => {
                          if (item.quantity <= 1) return;
                          setCart((current) =>
                            setQuickSaleQuantity(current, item.product_id, item.quantity - 1),
                          );
                        }}
                        aria-label={`Diminuir ${item.name}`}
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <QuantityInput
                        item={item}
                        onChange={(quantity) =>
                          setCart((current) =>
                            setQuickSaleQuantity(current, item.product_id, quantity),
                          )
                        }
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-9 w-9"
                        onClick={() =>
                          setCart((current) =>
                            setQuickSaleQuantity(current, item.product_id, item.quantity + 1),
                          )
                        }
                        aria-label={`Aumentar ${item.name}`}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="font-bold">{formatBRL(item.quantity * item.unit_price)}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* FECHAMENTO — o total repetido aqui NÃO é redundância de layout: o
            hero rola para fora da tela enquanto o operador monta o carrinho, e
            o número precisa estar à vista no instante em que ele confirma. É o
            mesmo arranjo do `QuoteForm`, que tem o card de totais com o hero
            mostrando o mesmo valor. */}
        <div className={cn(CONTENT_CARD, 'border-primary/30 p-4')}>
          <div className="space-y-4">
            <div className="flex items-end justify-between gap-3">
              <span className="shrink-0 text-sm text-muted-foreground">Total da venda</span>
              <span className="truncate text-2xl font-bold text-primary">{formatBRL(total)}</span>
            </div>

            <div className="space-y-2">
              <FieldLabel>Forma de pagamento</FieldLabel>
              <Select
                value={paymentMethod}
                onValueChange={(value) => setPaymentMethod(value as PaymentMethod)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((method) => (
                    <SelectItem key={method} value={method}>
                      {PAYMENT_METHOD_LABELS[method]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button3D
              className="w-full"
              disabled={!canFinalizeQuickSale(cart) || finalizeSale.isPending || !canWrite}
              onClick={() => setConfirmOpen(true)}
            >
              <ShoppingCart className="mr-2 h-5 w-5" />
              Finalizar venda
            </Button3D>
          </div>
        </div>
        </div>
        </div>
      </div>

      <CatalogSelector open={catalogOpen} onOpenChange={setCatalogOpen} onSelect={handleCatalogSelect} />
      <ClientSelector open={clientOpen} onOpenChange={setClientOpen} onSelect={setClient} />

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar venda de {formatBRL(total)}?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p>Ao confirmar, o Orçamigo vai:</p>
            <ul className="list-disc space-y-1 pl-5">
              <li>Criar um Pedido de venda já concluído.</li>
              <li>
                Baixa de estoque:{' '}
                {stockLines.length === 0
                  ? 'nenhum item rastreado'
                  : `${stockLines.length} ${stockLines.length === 1 ? 'item' : 'itens'}`}
                .
              </li>
              <li>
                {canSeeFinance
                  ? `Lançar uma receita paga em ${PAYMENT_METHOD_LABELS[paymentMethod]}.`
                  : 'Concluir a venda sem lançamento financeiro (seu perfil não tem essa permissão).'}
              </li>
            </ul>
            {negativeStock.length > 0 && (
              <div className="rounded-md bg-amber-50 p-3 text-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
                Saldo ficará negativo para {negativeStock.map((line) => line.name).join(', ')}. A venda
                não será bloqueada.
              </div>
            )}
            {!isOnline && (
              <div className="rounded-md bg-muted p-3">
                Sem conexão: pedido, estoque e financeiro serão sincronizados quando a internet voltar.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button3D color="gray" onClick={() => setConfirmOpen(false)}>
              Voltar
            </Button3D>
            <Button3D onClick={handleFinalize} disabled={finalizeSale.isPending}>
              {finalizeSale.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirmar venda
            </Button3D>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}
